import { Flex, Text } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { useRecoilState, useSetRecoilState, useRecoilValue } from "recoil";
import { useRef, useEffect, useState, useCallback } from "react";
import { GoTriangleRight } from "react-icons/go";
import { GoTriangleLeft } from "react-icons/go";
import { FaPlay } from "react-icons/fa";

import { analytics } from "MVVM/Analytics";
import { Button } from "MVVM/Components/Button";
import {
  ActivePlaylistClipState,
  FallbackPlaylistTypeState,
  WatchedClipsCountState,
  PlaylistLayoutState,
} from "MVVM/State/embeddablePlaylist";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";

import { HorizontalThumbnail } from "../HorizontalThumbnail";
import {
  formatPlaylistClip,
  getPlayerName,
  isPartner,
  Partner,
} from "../../Utilities";
import { PlaylistClip, PlaylistClips } from "../../@types";
import { usePlaylistRefresh } from "MVVM/Hooks/usePlaylistRefresh";
import { BlockedClipState } from "MVVM/State/embeddablePlaylist";
import { formatNumber } from "MVVM/Utilities/Clip";
import "./index.css";

const sharedArrowStyles = css`
  align-items: center;
  background: linear-gradient(270deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.5));
  cursor: pointer;
  height: 100%;
  justify-content: center;
  opacity: 0.5;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 300ms ease;
  width: 20px;
  z-index: 1;
`;

export const ThumbnailImage = styled.img<{
  $isHorizontalLayout: boolean;
}>`
  aspect-ratio: 16/9;
  border-radius: 4px;
  height: auto;
  max-width: 100%;
  object-fit: cover;
  width: 100%;

  ${({ $isHorizontalLayout }) =>
    $isHorizontalLayout &&
    `
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
    width: 100%;
  `}
`;

export const Thumbnail = styled(Flex)<{
  $active: boolean;
  $renderLolPlaylist: boolean;
  $isHorizontalLayout: boolean;
}>`
  aspect-ratio: 16/9;
  border: ${({ $active, $renderLolPlaylist, theme }) => {
    if (!$active) return "1.5px solid inherit";
    return `1.5px solid ${$renderLolPlaylist ? "#3273FA" : theme.colors.envy}`;
  }};
  border-radius: 4px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  flex: 0 0 auto;
  height: auto;
  max-width: 100%;
  overflow: hidden;
  position: relative;

  ${({ $isHorizontalLayout }) =>
    $isHorizontalLayout &&
    `
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
    width: 100%;
  `}
`;

const StyledThumbnailContainer = styled(Flex)`
  -ms-overflow-style: none;
  cursor: pointer;
  flex-wrap: nowrap;
  gap: 12px;
  height: 100%;
  overflow-x: scroll;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledViewIcon = styled(FaPlay)`
  color: ${({ theme }) => theme.colors.ice};
  height: 10px;
  width: 10px;
`;

const SocialItem = styled(Flex)`
  align-items: center;
  background-color: rgba(18, 22, 33, 0.55);
  backdrop-filter: blur(4px);
  border-top-left-radius: 4px;
  border-bottom-right-radius: 4px;
  flex-shrink: 0;
  gap: 6px;
  padding: 3px 6px;
`;

const StyledSocialInfo = styled(Flex)<{
  $isHorizontalLayout: boolean;
  $renderTopViewedList: boolean;
}>`
  align-items: center;
  bottom: 0px;
  gap: 8px;
  position: absolute;
  right: 0;

  ${({ $renderTopViewedList }) =>
    $renderTopViewedList &&
    `
      container-name: social-info;
      container-type: size;
      height: 22px;
      justify-content: flex-end;
      width: 100%;
  `}

  @media (max-width: 550px) {
    display: none;
  }
`;

const SocialInfo = ({
  thumbnail,
  isHorizontalLayout,
  renderTopViewedList = false,
}: {
  thumbnail: PlaylistClip;
  isHorizontalLayout: boolean;
  renderTopViewedList?: boolean;
}) => {
  const { track } = useAnalyticsContext();

  return (
    <StyledSocialInfo
      onClick={() => {
        track("Button - Click", {
          locationIdentifier: "thumbnail_social_click",
          location: "embeddable_playlist",
        });
        const searchParams = new URLSearchParams(window.location.search);
        const playlistId = searchParams.get("playlist");
        const utmSource = searchParams.get("platform") || "unknown";

        const ajs_aid = window.rudderanalytics.getAnonymousId();
        window.open(
          `${window.location.origin}/clip/${thumbnail.shareId}?ajs_aid=${ajs_aid}&playlistId=${playlistId}&context=playlist&utm_source=${utmSource}`,
          "_blank",
        );
      }}
      $isHorizontalLayout={isHorizontalLayout}
      $renderTopViewedList={renderTopViewedList}
    >
      <SocialItem>
        <StyledViewIcon className="social-icon" />
        <Text className="social-text">{formatNumber(thumbnail.views)}</Text>
      </SocialItem>
    </StyledSocialInfo>
  );
};

export const ThumbnailRow = ({
  thumbnails,
  renderLolPlaylist,
  onWatchLimitReached,
  className,
  platform,
}: {
  thumbnails: PlaylistClips;
  renderLolPlaylist: boolean;
  onWatchLimitReached?: (playerName: string, clickedClip: PlaylistClip) => void;
  className?: string;
  platform?: string;
}) => {
  const [activePlaylistClip, setActivePlaylistClip] = useRecoilState(
    ActivePlaylistClipState,
  );
  const [watchedClipsCount, setWatchedClipsCount] = useRecoilState(
    WatchedClipsCountState,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const setFallbackPlaylistType = useSetRecoilState(FallbackPlaylistTypeState);
  const { track } = useAnalyticsContext();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const playlistLayout = useRecoilValue(PlaylistLayoutState);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { refreshPlaylist } = usePlaylistRefresh();
  const isUGG = isPartner(platform, Partner.UGG);
  const isOPGG = isPartner(platform, Partner.OPGG);
  const setBlockedClip = useSetRecoilState(BlockedClipState);

  const thumbnailRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const renderTopViewedList = new URLSearchParams(window.location.search).get(
    "topViewedList",
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      const isAtLeftEdge = container.scrollLeft === 0;
      const isAtRightEdge =
        container.scrollLeft + container.clientWidth >= container.scrollWidth;

      // Only prevent parent scroll if we can scroll horizontally
      if ((e.deltaY > 0 && !isAtRightEdge) || (e.deltaY < 0 && !isAtLeftEdge)) {
        e.preventDefault();
        e.stopPropagation();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!thumbnails) return <></>;

  const handleThumbnailClick = (thumbnail: PlaylistClip) => {
    const formattedClip = formatPlaylistClip(thumbnail);

    if (watchedClipsCount >= 2 && onWatchLimitReached) {
      const playerName = getPlayerName({
        metadata: thumbnail.metadata.map((m) => ({
          key: String(m.key || ""),
          value: String(m.value || ""),
        })),
      });

      const shouldUpdateActiveClip = watchedClipsCount >= 2;

      if (shouldUpdateActiveClip) {
        setActivePlaylistClip(formattedClip);
      }

      onWatchLimitReached(playerName || thumbnail.username || "", thumbnail);

      return;
    }

    track("Button - Click", {
      locationIdentifier: "thumbnail_click",
      location: "embeddable_playlist",
    });
    setFallbackPlaylistType("");
    setActivePlaylistClip(formattedClip);
    setWatchedClipsCount((prev) => prev + 1);
    setBlockedClip(null);
  };

  const updateArrows = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => updateArrows();
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [updateArrows]);

  const scrollByAmount = (amount: number) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const scrollPageLeft = () => {
    const el = containerRef.current;
    if (!el) return;
    scrollByAmount(-(el.clientWidth * 0.9));
  };

  const scrollPageRight = () => {
    const el = containerRef.current;
    if (!el) return;
    scrollByAmount(el.clientWidth * 0.9);
  };

  useEffect(() => {
    const onResize = () => updateArrows();
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [updateArrows]);

  useEffect(() => {
    if (!activePlaylistClip || !containerRef.current) return;

    const thumbnailElement =
      thumbnailRefs.current[activePlaylistClip.internalId];

    if (!thumbnailElement) return;

    const THUMBNAIL_GAP = 12;

    const container = containerRef.current;
    const thumbnailLeft = thumbnailElement.offsetLeft;
    const thumbnailWidth = thumbnailElement.offsetWidth;
    const containerScrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const thumbnailStart = thumbnailLeft;
    const thumbnailEnd = thumbnailLeft + thumbnailWidth;
    const visibleStart = containerScrollLeft;
    const visibleEnd = containerScrollLeft + containerWidth;

    const isFullyVisible =
      thumbnailStart >= visibleStart && thumbnailEnd <= visibleEnd;

    if (isFullyVisible) return;

    let scrollTo: number;

    if (thumbnailEnd > visibleEnd) {
      scrollTo =
        thumbnailLeft - containerWidth + thumbnailWidth + THUMBNAIL_GAP;
    } else {
      scrollTo = thumbnailLeft - THUMBNAIL_GAP;
    }

    container.scrollTo({
      left: scrollTo,
      behavior: "smooth",
    });
  }, [activePlaylistClip]);

  const handleLoadMoreClips = async () => {
    analytics.track("Load More Playlist Clips - Thumbnail Button");
    setIsLoadingMore(true);

    refreshPlaylist();
    setIsLoadingMore(false);
  };

  const renderThumbnailRows = () =>
    thumbnails.map((thumbnail: PlaylistClip, index: number) => {
      if (playlistLayout === "horizontal")
        return (
          <>
            <HorizontalThumbnail
              ref={(el) => (thumbnailRefs.current[thumbnail.internalId] = el)}
              thumbnail={thumbnail}
              handleThumbnailClick={handleThumbnailClick}
              activePlaylistClip={activePlaylistClip}
              renderLolPlaylist={renderLolPlaylist}
              socialInfo={
                <SocialInfo thumbnail={thumbnail} isHorizontalLayout={true} />
              }
            />
            {index + 1 === thumbnails.length && (
              <StyledLoadMoreButton
                onClick={handleLoadMoreClips}
                disabled={isLoadingMore}
                $backgroundColor={isUGG ? "#3273FA" : isOPGG ? "#5383E8" : ""}
                $hoverColor={isUGG ? "#2963DA" : isOPGG ? "#4269BA" : ""}
              >
                {isLoadingMore ? "Loading..." : "Load More Clips"}
              </StyledLoadMoreButton>
            )}
          </>
        );

      return (
        <>
          <Thumbnail
            ref={(el) => (thumbnailRefs.current[thumbnail.internalId] = el)}
            key={thumbnail._id}
            onClick={() => handleThumbnailClick(thumbnail)}
            $renderLolPlaylist={renderLolPlaylist}
            $active={
              activePlaylistClip
                ? activePlaylistClip.internalId === thumbnail.internalId
                : false
            }
            $isHorizontalLayout={playlistLayout === "horizontal"}
          >
            <ThumbnailImage
              src={
                thumbnail.thumbnailOverlayUrl || thumbnail.thumbnailStandardUrl
              }
              loading="lazy"
              $isHorizontalLayout={playlistLayout === "horizontal"}
            />
            <SocialInfo
              thumbnail={thumbnail}
              isHorizontalLayout={false}
              renderTopViewedList={!!renderTopViewedList}
            />
          </Thumbnail>
          {index + 1 === thumbnails.length && (
            <StyledLoadMoreButton
              onClick={handleLoadMoreClips}
              disabled={isLoadingMore}
              $backgroundColor={isUGG ? "#3273FA" : isOPGG ? "#5383E8" : ""}
              $hoverColor={isUGG ? "#2963DA" : isOPGG ? "#4269BA" : ""}
            >
              {isLoadingMore ? "Loading..." : "Load More Clips"}
            </StyledLoadMoreButton>
          )}
        </>
      );
    });

  return (
    <RowWrapper
      className={className}
      $isHorizontalLayout={playlistLayout === "horizontal"}
    >
      {canScrollLeft && (
        <StyledArrowBackground className="arrow" onClick={scrollPageLeft}>
          <StyledArrowLeft />
        </StyledArrowBackground>
      )}
      <StyledThumbnailContainer ref={containerRef}>
        {renderThumbnailRows()}
      </StyledThumbnailContainer>
      {canScrollRight && (
        <StyledArrowBackground2 className="arrow" onClick={scrollPageRight}>
          <StyledArrowRight />
        </StyledArrowBackground2>
      )}
    </RowWrapper>
  );
};

const RowWrapper = styled(Flex)<{ $isHorizontalLayout: boolean }>`
  position: relative;
  overflow: hidden;

  ${({ $isHorizontalLayout }) =>
    $isHorizontalLayout &&
    `
      padding: 20px 0;
      flex: 1;
    `}

  &:hover .arrow {
    opacity: 1;
    pointer-events: auto;
  }
`;

const StyledArrowBackground = styled(Flex)`
  ${sharedArrowStyles}
  left: 0;
`;

const StyledArrowBackground2 = styled(Flex)`
  ${sharedArrowStyles}
  right: 0;
`;

const StyledArrowRight = styled(GoTriangleRight)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  width: 20px;
`;

const StyledArrowLeft = styled(GoTriangleLeft)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  width: 20px;
`;

const StyledLoadMoreButton = styled(Button)<{
  $backgroundColor: string;
  $hoverColor: string;
}>`
  font-size: 12px;
  height: 2rem;
  line-height: 14px;
  margin: auto 0;
  min-width: 110px;
  padding: 8px;
  text-align: center;
  width: 100%;

  @media (max-width: 380px) {
    font-size: 10px;
    height: 1.5rem;
    line-height: 12px;
    min-width: 80px;
    padding: 6px;
  }

  ${({ $backgroundColor }) =>
    $backgroundColor &&
    `
    background-color: ${$backgroundColor};
  `}

  ${({ $hoverColor }) =>
    $hoverColor &&
    `
    :hover {
      background-color: ${$hoverColor}
    }
  `}
`;
